"use client";

import React, { useState } from "react";
import { Globe, MapPin, Navigation, Activity, Server, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const hubs = [{
  id: "hub-medellin",
  region: "Medellin, Colombia",
  location: "A sunrise over the coffee farms.",
  coordinates: {
    x: 18,
    y: 42
  },
  status: "Founder's Choice",
  capacity: "Boutique",
  latency: "Vibrant",
  type: "SIGNATURE EXPERIENCE"
}, {
  id: "hub-naxos",
  region: "Naxos, Greece",
  location: "Mark's study of the Portara at dusk.",
  coordinates: {
    x: 52,
    y: 32
  },
  status: "Timeless",
  capacity: "Ancient",
  latency: "Serene",
  type: "ISLAND ESCAPE"
}, {
  id: "hub-havana",
  region: "Havana, Cuba",
  location: "Delivering supplies to local schools.",
  coordinates: {
    x: 28,
    y: 35
  },
  status: "Impact",
  capacity: "Historic",
  latency: "Soulful",
  type: "PHILANTHROPIC"
}, {
  id: "hub-cartagena",
  region: "Cartagena, Colombia",
  location: "A midnight walk through Getsemaní.",
  coordinates: {
    x: 54,
    y: 22
  },
  status: "Luxury",
  capacity: "Colonial",
  latency: "Electric",
  type: "COLONIAL GEM"
}, {
  id: "hub-san-juan",
  region: "San Juan, PR",
  location: "Family reunions on the cobblestones.",
  coordinates: {
    x: 35,
    y: 75
  },
  status: "Heritage",
  capacity: "Tropical",
  latency: "Warm",
  type: "CARIBBEAN RETREAT"
}, {
  id: "hub-palomino",
  region: "Palomino, Colombia",
  location: "Floating down the river at dawn.",
  coordinates: {
    x: 85,
    y: 38
  },
  status: "Discovery",
  capacity: "Eco-Luxury",
  latency: "Raw",
  type: "NATURE SANCTUARY"
}, {
  id: "hub-tayrona",
  region: "Tayrona, Colombia",
  location: "Pristine sands and jungle paths.",
  coordinates: {
    x: 78,
    y: 58
  },
  status: "Adventure",
  capacity: "Wild",
  latency: "Untamed",
  type: "COLLECTIVE DISCOVERY"
}];
export default function DistributionHubs() {
  const [selectedHubId, setSelectedHubId] = useState<string>(hubs[0].id);
  const selectedHub = hubs.find(h => h.id === selectedHubId) || hubs[0];
  return <section data-section-id="192920" id="our-footprint" className="dark bg-background text-foreground relative w-full overflow-hidden border-y border-border">
      <div className="relative flex min-h-[600px] flex-col bg-background overflow-hidden">
        
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]"></div>
        </div>

        <div className="relative z-10 flex-1 w-full h-full">
          <div className="absolute inset-0 transition-transform duration-1000 ease-in-out" style={{
          transform: `translate(${(50 - selectedHub.coordinates.x) * 0.2}%, ${(50 - selectedHub.coordinates.y) * 0.2}%) scale(1.05)`
        }}>
            <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none text-secondary" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M10,20 Q20,15 30,25 T50,30 T70,20 T90,35" fill="none" stroke="currentColor" strokeWidth="0.2" />
              <path d="M15,40 Q25,45 35,35 T55,50 T75,45 T95,60" fill="none" stroke="currentColor" strokeWidth="0.2" />
              <path d="M20,60 Q30,70 40,55 T60,65 T80,50 T90,75" fill="none" stroke="currentColor" strokeWidth="0.2" />
            </svg>

            {hubs.map((hub, i) => {
            const isSelected = selectedHubId === hub.id;
            return <div key={hub.id} data-index={i} className="absolute z-20 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group" style={{
              left: `${hub.coordinates.x}%`,
              top: `${hub.coordinates.y}%`
            }} onClick={() => setSelectedHubId(hub.id)}>
                  <div className={`absolute inset-0 rounded-full bg-secondary opacity-20 ${isSelected ? "animate-ping scale-150" : "group-hover:animate-ping"}`} style={{
                animationDuration: '3s'
              }}></div>
                  
                  <div className={`relative flex items-center justify-center rounded-full border border-secondary/50 bg-background shadow-[0_0_15px_hsla(43,48%,59%,0.3)] transition-all duration-300 ${isSelected ? "size-6 shadow-[0_0_30px_hsl(var(--secondary))]" : "size-4 hover:size-5"}`}>
                    <div className={`rounded-full bg-secondary ${isSelected ? "size-2" : "size-1.5"}`}></div>
                  </div>

                  <div className={`absolute top-full left-1/2 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-border/50 bg-background/90 px-2 py-1 text-xs font-mono backdrop-blur-sm transition-opacity duration-300 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                    {hub.region}
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="absolute bottom-6 right-6 z-30 w-full max-w-sm px-4 md:px-0">
          <Card className="border-border/50 bg-background/80 backdrop-blur-xl shadow-2xl rounded-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardDescription className="font-mono text-xs uppercase tracking-widest text-secondary">
                  {selectedHub.type}
                </CardDescription>
                <div className="flex items-center gap-1.5 rounded-full border border-secondary/20 bg-secondary/10 px-2 py-0.5 text-[10px] font-mono font-medium text-secondary">
                  <span className="relative flex size-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75"></span>
                    <span className="relative inline-flex size-1.5 rounded-full bg-secondary"></span>
                  </span>
                  {selectedHub.status}
                </div>
              </div>
              <CardTitle className="text-2xl font-default">{selectedHub.region}</CardTitle>
              <div className="flex items-center gap-2 text-sm font-sans italic text-muted-foreground">
                <Navigation className="size-3 text-secondary" />
                <span>{selectedHub.location}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 rounded-sm border border-border/50 bg-muted/30 p-3">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-tighter text-muted-foreground">
                    <Activity className="size-3 text-secondary" />
                    <span>Essence</span>
                  </div>
                  <span className="font-sans text-lg font-medium text-foreground">{selectedHub.capacity}</span>
                </div>
                <div className="flex flex-col gap-1 rounded-sm border border-border/50 bg-muted/30 p-3">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-tighter text-muted-foreground">
                    <Zap className="size-3 text-secondary" />
                    <span>Aura</span>
                  </div>
                  <span className="font-sans text-lg font-medium text-foreground">{selectedHub.latency}</span>
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <Button variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-sans tracking-wide">
                  <Server className="mr-2 size-4" />
                  Explore Experience
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}